import { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlogCard from "../components/BlogCard";
import BlogSidebar from "../components/BlogSidebar";
import SEOHead from "../components/SEOHead";
import { BLOG_POSTS } from "../data/blogPosts";
import { ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import SkeletonCard from "../components/SkeletonCard";

import { useTranslation } from 'react-i18next';
const POSTS_PER_PAGE = 6;

function slugify(str) {
  return str.toLowerCase().replace(/\s+/g, "-");
}

function deslugify(slug) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const CATEGORY_DESCRIPTIONS = {
  "AI Agents": "Deep dives into AI agent technology, architecture, deployment strategies, and real-world applications.",
  "Tutorials": "Step-by-step guides to help you set up, configure, and optimize AI agents for your business.",
  "Case Studies": "Real-world examples of businesses deploying AI agents — with metrics, lessons, and outcomes.",
  "Integrations": "Connect your AI agents to CRMs, helpdesks, calendars, and other business tools.",
  "Industry News": "The latest trends, research, and developments in the AI agent industry.",
};

export default function BlogCategory() {
  const { t } = useTranslation();
  const { category: slug } = useParams();
  const categorySlug = decodeURIComponent(slug || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [categorySlug]);

  // Resolve slug to actual category name from data
  const categoryName = useMemo(() => {
    // Try matching slugified category name against URL slug
    const directMatch = BLOG_POSTS.find(
      (p) => p.category && slugify(p.category) === categorySlug
    );
    if (directMatch) return directMatch.category;
    // Fallback: deslugify and try case-insensitive match
    const deslugified = deslugify(categorySlug);
    const ciMatch = BLOG_POSTS.find(
      (p) => p.category && p.category.toLowerCase() === deslugified.toLowerCase()
    );
    if (ciMatch) return ciMatch.category;
    return deslugified;
  }, [categorySlug]);

  const categoryPosts = useMemo(() => {
    return BLOG_POSTS.filter(
      (p) => p.category === categoryName
    );
  }, [categoryName]);

  const totalPages = Math.max(1, Math.ceil(categoryPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagePosts = categoryPosts.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  );

  const description = CATEGORY_DESCRIPTIONS[categoryName] ||
    `Explore all ${categoryName} articles on the Max Employee blog. Guides, insights, and case studies on AI digital workers.`;

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://maxemployee.com/blog" },
      { "@type": "ListItem", position: 3, name: categoryName, item: `https://maxemployee.com/blog/category/${slugify(categoryName)}` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={`${categoryName} Articles | Max Employee Blog`}
        description={description}
        url={`/blog/category/${slugify(categoryName)}`}
        structuredData={breadcrumbStructuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-zinc-900 bg-black">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#09090B]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-8 sm:pb-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-6 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-amber-500" aria-hidden="true" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
              Category
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.9] text-white mb-4">
            {categoryName}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            {description}
          </p>
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {categoryPosts.length} article{categoryPosts.length !== 1 ? "s" : ""}
          </div>
        </div>
      </section>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Main content */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog_category.loading_blog_posts')}>
                {Array.from({ length: POSTS_PER_PAGE }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : pagePosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog_category.blog_posts')}>
                {pagePosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-20" role="status" aria-live="polite">
                <p className="font-display text-xl sm:text-2xl uppercase text-zinc-400 mb-4">{t('blog_category.no_articles_in_this_category')}</p>
                <p className="text-zinc-400 text-sm mb-6">{t('blog_category.check_back_soon_or_explore_other_categories')}</p>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  View all articles
                </Link>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2 sm:gap-4 mt-8 sm:mt-12" aria-label={t('blog_category.pagination')}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog_category.previous_page')}
                >
                  <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t('blog_category.prev')}</span>
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog_category.next_page')}
                >
                  <span className="hidden sm:inline">{t('blog_category.next')}</span> <ChevronRight className="w-4 h-4" />
                </button>
              </nav>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 order-1 lg:order-2" aria-label={t('blog_category.blog_sidebar')}>
            <BlogSidebar />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
